import { LOCAL_STORAGE_UTM_PARAMS_KEY } from '@lib/constants/tracking'
import { getFromLocalStorage } from '@lib/utils/local-storage'
import { NextRouter } from 'next/router'
import { parseCookies, setCookie } from 'nookies'

const UTM_PARAMETERS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
]

const CLICK_ID_PARAMETERS = ['gclid', 'fbclid'] as const
type ClickIdParam = typeof CLICK_ID_PARAMETERS[number]
type ClickIdMap = Partial<Record<ClickIdParam, string>>

/**
 * Ad-network params the Facebook landing pages append alongside the utm_* set:
 *   cid={{campaign.id}}&plat={{site_source_name}}&plc={{placement}}
 *
 * They have no dedicated backend columns and land in `LeadSource.extra`.
 * Keys stay lowercase and underscore-free so the backend's camelCase parser
 * and renderer leave them byte-identical in both directions.
 */
const AD_PARAMETERS = ['cid', 'plat', 'plc'] as const

/**
 * The key these are stored under, in both the localStorage blob and the
 * outgoing `leadSource` payload. The blob is keyed by *payload* key and gets
 * spread straight into `leadSource` / `recentLeadSource`, which is why adding
 * a param here needs no change at any of the payload sites.
 */
const AD_PARAMS_KEY = 'extra'

/** Cookie holding the ad params as JSON, for the localStorage-failure path. */
const AD_PARAMS_COOKIE = 'cg_ad_params'

/**
 * Cookie the static landing pages (static.codingal.com) set on `.codingal.com`
 * for 90 days, the same way they store gclid/fbclid. It holds one click's
 * whole attribution set - the five utm_* plus cid/plat/plc - as flat JSON.
 * See `web-static/assets/click-ids.js`.
 */
const LANDING_PAGE_ATTRIBUTION_COOKIE = 'cg_lp_attribution'

/**
 * Read the landing pages' attribution set in `leadSource` shape. Used only
 * when this app has no attribution of its own, and always taken whole: mixing
 * it with another touch's utm_* would let its `plat` corroborate a different
 * click's utm_source in the backend's normalization.
 */
const getLandingPageAttributionFromCookie = (cookies: {
  [key: string]: string
}) => {
  const stored = cookies[LANDING_PAGE_ATTRIBUTION_COOKIE]
  if (!stored) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(stored)
  } catch {
    // A malformed cookie must not take the whole lead source down with it.
    return null
  }
  if (!parsed || typeof parsed !== 'object') return null

  const readString = (name: string) => {
    const value = parsed[name]
    return typeof value === 'string' && value ? value : undefined
  }
  const returnObj = {}
  UTM_PARAMETERS.forEach((name) => {
    const value = readString(name)
    if (value !== undefined) returnObj[name] = value
  })
  const adParams = {}
  AD_PARAMETERS.forEach((name) => {
    const value = readString(name)
    if (value !== undefined) adParams[name] = value
  })
  if (Object.keys(adParams).length > 0) returnObj[AD_PARAMS_KEY] = adParams

  if (Object.keys(returnObj).length === 0) return null
  return returnObj
}

/** Read one query param, taking the last value when a key repeats. */
const readQueryParam = (router: NextRouter, name: string) => {
  const value = router.query[name]
  return Array.isArray(value) ? value[value.length - 1] : value
}

/**
 * True when the URL looks like a campaign touch rather than an ordinary page
 * visit. `cid` and `plc` are near-universal names in email tools and ad
 * servers, so on their own they are as likely to be someone else's parameter
 * as ours - we only trust them next to a utm_* param or a click ID.
 */
const isCampaignTouch = (router: NextRouter) =>
  UTM_PARAMETERS.some((name) => router.query[name] !== undefined) ||
  router.query['fbclid'] !== undefined

/**
 * True when this touch re-identifies where the traffic came from. Any ad
 * params already stored then belong to an earlier click and must be replaced
 * wholesale - including with nothing, when the new touch carries none.
 */
const reidentifiesSource = (router: NextRouter) =>
  router.query['utm_source'] !== undefined

/** Collect the ad params present on the current URL. */
const collectAdParams = (router: NextRouter) => {
  const adParams = {}
  if (!isCampaignTouch(router)) return adParams
  AD_PARAMETERS.forEach((name) => {
    const value = readQueryParam(router, name)
    if (value !== undefined) adParams[name] = value
  })
  return adParams
}

export const storeLastTouchUTMParametersInSessionCookie = (
  router: NextRouter
) => {
  UTM_PARAMETERS.forEach((paramName) => {
    const paramValue = router.query[paramName]
    if (paramValue !== undefined) {
      setCookie({}, paramName, paramValue.toString(), {
        maxAge: 24 * 60 * 60,
        path: '/',
      })

      if (paramName === 'utm_source') {
        if (!parseCookies()['utm_source'])
          import('@sentry/node').then((s) =>
            s.captureMessage(`${paramName} = ${paramValue} not captured!`)
          )
      }
    }
  })

  // One namespaced cookie for the whole tuple rather than one per param.
  // Namespaced because `cid` / `plat` / `plc` are generic enough to collide
  // with a third-party script's cookie, and host-only (no `domain`) so it can
  // never shadow, or be shadowed by, a cookie set on a parent domain.
  // Written whenever the touch re-identifies the source, not only when ad
  // params are present - otherwise a later non-FB touch refreshes the utm_*
  // cookies and leaves the previous click's tuple behind, and the cookie
  // fallback then hands back a mixed record. That path is live in normal use:
  // `pages/trial-class/track-booking.tsx` clears the localStorage blob after a
  // booking but not these cookies.
  const adParams = collectAdParams(router)
  if (Object.keys(adParams).length > 0 || reidentifiesSource(router)) {
    setCookie({}, AD_PARAMS_COOKIE, JSON.stringify(adParams), {
      maxAge: 24 * 60 * 60,
      path: '/',
    })
  }
}

export const generateUTMParametersDictFromCookie = () => {
  const returnObj = {}
  const cookies = parseCookies()
  UTM_PARAMETERS.forEach((paramName) => {
    const paramValue = cookies[paramName]
    if (paramValue !== undefined) {
      returnObj[paramName] = paramValue
    }
  })

  const storedAdParams = cookies[AD_PARAMS_COOKIE]
  if (storedAdParams) {
    try {
      returnObj[AD_PARAMS_KEY] = JSON.parse(storedAdParams)
    } catch {
      // A malformed cookie must not take the whole lead source down with it.
    }
  }

  // Nothing captured on this app, e.g. the visitor reached it from a landing
  // page by a plain link rather than the signup form. Fall back to the set the
  // landing page stored, as the click IDs already do.
  if (Object.keys(returnObj).length === 0)
    return getLandingPageAttributionFromCookie(cookies)
  return returnObj
}

export const generateUTMParametersDict = (router: NextRouter) => {
  const returnObj = {}
  UTM_PARAMETERS.forEach((paramName) => {
    let paramValue = router.query[paramName]

    if (paramValue === undefined) return

    // Handling case: Multiple instances of the same UTM param key is present in the query
    // In that case, use the last query param value.
    // Example: `/utm_medium=test&utm_medium=test2`
    if (Array.isArray(paramValue)) paramValue = paramValue.pop()

    returnObj[paramName] = paramValue
  })

  const adParams = collectAdParams(router)

  // Nothing attribution-related on this URL, so leave whatever is already
  // stored alone (an internal navigation must not wipe the landing touch).
  if (Object.keys(returnObj).length === 0 && Object.keys(adParams).length === 0)
    return null

  // Whole-tuple replace rather than a per-key merge. The ad params only mean
  // anything alongside the utm_* set from the SAME click, so a fresh touch
  // that carries no ad params must clear the previous one's rather than let
  // them ride along under a new utm_source. That matters beyond reporting:
  // `plat` corroborates the backend's utm_source normalization, so a mixed
  // record could drive a wrong normalization, not just a wrong report.
  //
  // Gated on the touch actually re-identifying the source, because the utm_*
  // merge above is per-key: an incidental `?utm_content=footer-cta` leaves the
  // Meta click's utm_source in place, so clearing its `plat` there would strip
  // the corroboration off a still-valid source and drop the lead back to a
  // bare numeric utm_source. The merge stays per-key deliberately - making it
  // wholesale would let a single stray utm param erase utm_source itself and
  // turn a paid lead into an unattributed one, which is worse.
  if (Object.keys(adParams).length > 0 || reidentifiesSource(router))
    returnObj[AD_PARAMS_KEY] = adParams

  const prevUTMParameters = getUTMParametersDictFromLocalStorage()
  return { ...prevUTMParameters, ...returnObj }
}

export const getUTMParametersDictFromLocalStorage = () => {
  const utmParametersDict = getFromLocalStorage(LOCAL_STORAGE_UTM_PARAMS_KEY)
  if (!utmParametersDict) return null

  const utmParametersObj = JSON.parse(utmParametersDict)
  if (!utmParametersObj) return null

  // Handling case: Multiple instances of the same UTM param key is present in the query
  // In that case, use the last query param value.
  // Example: `/utm_medium=test&utm_medium=test2`
  for (const [key, value] of Object.entries(utmParametersObj)) {
    if (Array.isArray(value)) utmParametersObj[key] = value.pop()
  }

  return utmParametersObj
}

export const storeClickIDParametersInSessionCookie = (router: NextRouter) => {
  const cookies = parseCookies()
  CLICK_ID_PARAMETERS.forEach((paramName) => {
    let paramValue = router.query[paramName]

    if (paramValue === undefined) {
      // Fall back to .codingal.com domain cookie (set by static.codingal.com)
      paramValue = cookies[paramName]
    }

    if (paramValue !== undefined) {
      const value = Array.isArray(paramValue)
        ? paramValue[paramValue.length - 1]
        : paramValue
      // Store on current domain (codingal.com)
      setCookie({}, paramName, value, { maxAge: 90 * 24 * 60 * 60, path: '/' })
    }
  })
}

export const getClickIDParametersDictFromCookie = (): ClickIdMap | null => {
  const cookies = parseCookies()
  const returnObj: ClickIdMap = {}
  CLICK_ID_PARAMETERS.forEach((paramName) => {
    const paramValue = cookies[paramName]
    if (paramValue !== undefined) {
      returnObj[paramName] = paramValue
    }
  })
  if (Object.keys(returnObj).length === 0) return null
  return returnObj
}

export const generateClickIDParametersDict = (
  router: NextRouter
): ClickIdMap | null => {
  const returnObj: ClickIdMap = {}
  CLICK_ID_PARAMETERS.forEach((paramName) => {
    let paramValue = router.query[paramName]

    if (paramValue === undefined) {
      // Try to get from cookie if not in URL
      const cookies = parseCookies()
      paramValue = cookies[paramName]
    }

    if (paramValue === undefined) return

    // Handling case: Multiple instances of the same param key
    if (Array.isArray(paramValue)) paramValue = paramValue.pop()

    returnObj[paramName] = paramValue
  })

  if (Object.keys(returnObj).length === 0) return null
  return returnObj
}
